import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DayPicker,
  type DayButtonProps,
  type PropsBase,
  type PropsRange,
} from 'react-day-picker';
import { cn } from '@shared/lib/utils';

export type CalendarProps = PropsBase & PropsRange;

function CalendarDayButton({ day: _day, modifiers, className, ...props }: DayButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm text-foreground transition-colors duration-fast ease-standard hover:bg-accent',
        modifiers.today && 'border border-border',
        modifiers.outside && 'text-muted-foreground opacity-50',
        modifiers.disabled && 'pointer-events-none text-muted-foreground opacity-40',
        modifiers.selected &&
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
        modifiers.range_middle && 'rounded-none bg-accent text-accent-foreground hover:bg-accent',
        modifiers.range_start && 'rounded-l-lg bg-primary text-primary-foreground',
        modifiers.range_end && 'rounded-r-lg bg-primary text-primary-foreground',
        modifiers.range_start && modifiers.range_end && 'rounded-lg',
        className
      )}
      {...props}
    />
  );
}

const navButtonClassName =
  'inline-flex size-tap items-center justify-center rounded-lg border border-border text-foreground transition-colors duration-fast ease-standard hover:bg-accent sm:size-8';

export function Calendar({ className, classNames, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays
      className={cn('p-1', className)}
      classNames={{
        months: 'flex flex-col',
        month: 'relative space-y-3',
        month_caption: 'flex h-10 items-center justify-center',
        caption_label: 'text-sm font-medium text-foreground',
        nav: 'absolute inset-x-0 top-0 flex h-10 items-center justify-between px-1',
        button_previous: navButtonClassName,
        button_next: navButtonClassName,
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-9 text-center text-xs font-medium text-muted-foreground',
        week: 'mt-1 flex w-full',
        day: 'h-9 w-9 p-0 text-center text-sm',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: iconClassName }) => {
          const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
          return <Icon className={cn('h-4 w-4', iconClassName)} />;
        },
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  );
}
